import React, { useState, useEffect, useRef } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { Plus, Trophy, Star, Shield } from 'lucide-react';
import { Challenge } from '../../types/challenges';
import { auth } from '../../lib/firebase';
import { NewChallengeModal } from './NewChallengeModal';
import { ChallengeCard } from './ChallengeCard';
import { ChallengeDetailsModal } from './ChallengeDetailsModal';
import { CongratulationsModal } from './CongratulationsModal';
import { AdminDashboard } from '../admin/AdminDashboard';
import {
  joinChallenge,
  quitChallenge,
  subscribeToChallenges,
  closeChallenge,
  deleteChallenge
} from '../../lib/store';
import { logger } from '../../lib/logger';

interface ChallengesPageProps {
  challenges?: Challenge[];
}

export function ChallengesPage({ challenges: initialChallenges = [] }: ChallengesPageProps) {
  const [showNewChallenge, setShowNewChallenge] = useState(false);
  const [selectedChallenge, setSelectedChallenge] = useState<Challenge | null>(null);
  const [localChallenges, setLocalChallenges] = useState<Challenge[]>(initialChallenges);
  const [showCongratulations, setShowCongratulations] = useState(false);
  const [completedChallenge, setCompletedChallenge] = useState<Challenge | null>(null);
  const [showAdminDashboard, setShowAdminDashboard] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [subscriptionError, setSubscriptionError] = useState<string | null>(null);

  // Ref to track if component is mounted
  const isMounted = useRef(true);

  const [user, authLoading] = useAuthState(auth);
  const isAdmin = user?.email === 'breadflowbybadabec@gmail.com';

  const currentMonth = new Date().toLocaleString('en-US', { month: 'long' });

  // Subscribe to real-time challenge updates
  useEffect(() => {
    logger.info('ChallengesPage subscription effect triggered', {
      component: 'ChallengesPage',
      hasUser: !!user,
      userId: user?.uid,
      authLoading,
      isMounted: isMounted.current
    });

    // Wait for auth to finish loading
    if (authLoading) {
      logger.debug('Waiting for auth to finish loading', {
        component: 'ChallengesPage'
      });
      return;
    }

    // Guard against missing user
    if (!user || !auth.currentUser) {
      logger.debug('Skipping subscription setup - no user yet', {
        component: 'ChallengesPage',
        hasHookUser: !!user,
        hasAuthCurrentUser: !!auth.currentUser
      });
      setIsLoading(false);
      return;
    }

    // Reset mounted flag for this effect run
    isMounted.current = true;

    logger.info('Setting up challenges subscription', {
      component: 'ChallengesPage',
      userId: user.uid
    });

    const unsubscribe = subscribeToChallenges(
      (updatedChallenges) => {
        if (!isMounted.current) {
          logger.warn('Challenges update received after unmount', {
            component: 'ChallengesPage'
          });
          return;
        }

        logger.info('Challenges updated', {
          component: 'ChallengesPage',
          count: updatedChallenges.length
        });

        setLocalChallenges(updatedChallenges);
        setIsLoading(false);
        setSubscriptionError(null);

        if (selectedChallenge) {
          const updated = updatedChallenges.find(c => c.id === selectedChallenge.id);
          if (updated) {
            setSelectedChallenge(updated);
          }
        }
      },
      (error) => {
        if (!isMounted.current) return;

        logger.error('Challenges subscription error', {
          component: 'ChallengesPage',
          error: error.message
        });

        setIsLoading(false);
        setSubscriptionError('Failed to load challenges. Please check your connection and try again.');
      }
    );

    return () => {
      logger.debug('Cleaning up challenges subscription', {
        component: 'ChallengesPage'
      });
      isMounted.current = false;
      unsubscribe();
    };
  }, [user, authLoading, selectedChallenge]);

  // Filter challenges into different categories
  const userChallenges = localChallenges.filter(c => {
    const userParticipation = c.participants.find(p => p.userId === user?.uid);
    return c.isActive && userParticipation;
  });

  const otherChallenges = localChallenges.filter(c => 
    c.isActive && !c.participants.some(p => p.userId === user?.uid)
  );

  const completedChallenges = localChallenges.filter(c => {
    const userParticipation = c.participants.find(p => p.userId === user?.uid);
    return !c.isActive && userParticipation?.completed;
  });

  const handleJoinChallenge = async (challengeId: string) => {
    if (!user) return;

    try {
      await joinChallenge(challengeId);
    } catch (error) {
      console.error('Error joining challenge:', error);
    }
  };

  const handleQuitChallenge = async (challengeId: string) => {
    if (!user) return;

    try {
      await quitChallenge(challengeId);
      setSelectedChallenge(null);
    } catch (error) {
      console.error('Error quitting challenge:', error);
    }
  };

  const handleCloseChallenge = async (challengeId: string) => {
    try {
      await closeChallenge(challengeId);
    } catch (error) {
      console.error('Error closing challenge:', error);
    }
  };

  const handleDeleteChallenge = async (challengeId: string) => {
    try {
      await deleteChallenge(challengeId);
    } catch (error) {
      console.error('Error deleting challenge:', error);
    }
  };

  const handleChallengeClick = (challenge: Challenge) => {
    setSelectedChallenge(challenge);
  };

  const handleChallengeComplete = (challenge: Challenge) => {
    setCompletedChallenge(challenge);
    setShowCongratulations(true);
    setSelectedChallenge(null);
  };

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="flex justify-between items-start mb-8 mt-4">
        <div>
          <h1 className="text-3xl text-white font-brand font-light italic">Baking Challenges</h1>
          <p className="text-sm text-white mt-1">
            Join challenges, improve your skills, and share your results with the community.
          </p>
        </div>
        {isAdmin && (
          <div className="flex gap-2">
            <button
              onClick={() => setShowAdminDashboard(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Shield className="w-4 h-4" />
              Moderation
            </button>
            <button
              onClick={() => setShowNewChallenge(true)}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 flex items-center gap-2"
            >
              <Trophy className="w-4 h-4" />
              New Challenge
            </button>
          </div>
        )}
      </div>

      {/* Active Challenges */}
      {userChallenges.length > 0 && (
        <div className="space-y-4 mb-8">
          <h2 className="text-2xl text-white font-brand font-light italic flex items-center gap-2">
             {currentMonth}
          </h2>
          <div className="space-y-4 w-full">
            {userChallenges.map(challenge => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onJoin={handleJoinChallenge}
                onClose={handleCloseChallenge}
                onDelete={handleDeleteChallenge}
                userParticipating={true}
                onClick={() => handleChallengeClick(challenge)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Available Challenges */}
      {otherChallenges.length > 0 && (
        <div className="space-y-4 mb-8">
          <h2 className="text-2xl text-white font-brand font-light italic flex items-center gap-2">
             {currentMonth}
          </h2>
          <div className="space-y-4 w-full">
            {otherChallenges.map(challenge => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onJoin={handleJoinChallenge}
                onClose={handleCloseChallenge}
                onDelete={handleDeleteChallenge}
                userParticipating={false}
                onClick={() => handleChallengeClick(challenge)}
              />
            ))}
          </div>
        </div>
      )}

      {/* Completed Challenges */}
      {completedChallenges.length > 0 && (
        <div className="space-y-4 mt-4">
          <h2 className="text-2xl text-white font-brand font-light italic flex items-center gap-2">
            My Completed Challenges
          </h2>
          <div className="w-full space-y-4">
            {completedChallenges.map(challenge => (
              <ChallengeCard
                key={challenge.id}
                challenge={challenge}
                onJoin={handleJoinChallenge}
                onClose={handleCloseChallenge}
                onDelete={handleDeleteChallenge}
                userParticipating={true}
                onClick={() => handleChallengeClick(challenge)}
              />
            ))}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="text-center py-12">
          <div className="w-12 h-12 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <h3 className="text-lg font-medium mb-2 text-white">Loading Challenges...</h3>
          <p className="text-white/70">
            Fetching the latest baking challenges
          </p>
        </div>
      )}

      {!isLoading && subscriptionError && (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2 text-white">Error Loading Challenges</h3>
          <p className="text-white/70 mb-4">
            {subscriptionError}
          </p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && !subscriptionError && localChallenges.length === 0 && (
        <div className="text-center py-12">
          <Trophy className="w-12 h-12 text-amber-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium mb-2 text-white">No Active Challenges</h3>
          <p className="text-white/70">
            Check back soon for new baking challenges!
          </p>
        </div>
      )}

      {showNewChallenge && (
        <NewChallengeModal
          isOpen={showNewChallenge}
          onClose={() => setShowNewChallenge(false)}
        />
      )}

      {selectedChallenge && (
        <ChallengeDetailsModal
          challenge={selectedChallenge}
          onClose={() => setSelectedChallenge(null)}
          onJoin={handleJoinChallenge}
          onQuit={handleQuitChallenge}
          onComplete={handleChallengeComplete}
          userParticipating={userChallenges.some(c => c.id === selectedChallenge.id)}
        />
      )}

      {showCongratulations && completedChallenge && (
        <CongratulationsModal
          challenge={completedChallenge}
          isOpen={showCongratulations}
          onClose={() => {
            setShowCongratulations(false);
            setCompletedChallenge(null);
          }}
        />
      )}

      {showAdminDashboard && (
        <AdminDashboard
          onClose={() => setShowAdminDashboard(false)}
        />
      )}
    </div>
  );
}